/**
 * Leitura das variáveis de ambiente do front.
 *
 * Papel: valida as três `VITE_AUTH0_*`, sem as quais o login não começa, e
 * monta o prefixo das rotas da API a partir da `VITE_API_URL`.
 *
 * Como usar: toda variável que o navegador precisar deverá ser lida aqui,
 * declarada em `vite-env.d.ts`, criada como variable do repositório no GitHub e
 * repassada no `env:` do passo "Construir o front" do `deploy.yml`. Faltando o
 * repasse, o build de produção recebe a variável vazia sem acusar nada, e o
 * defeito só aparece no navegador. Só entra o que é público por natureza:
 * o Vite embute no bundle tudo que tem o prefixo `VITE_`, e qualquer pessoa que
 * abrir o navegador lê o valor. Segredo pertence às application settings do
 * Function App e nunca passa por este arquivo.
 *
 * `lerConfiguracaoAuth0` devolve um resultado em vez de lançar. Uma exceção no
 * carregamento do módulo produziria tela branca com um erro só no console, e o
 * resultado explícito é o que permite ao `ProvedorAuth` desenhar um painel
 * dizendo exatamente qual variável falta.
 */
import { z } from 'zod';

const configuracaoAuth0Schema = z.object({
  VITE_AUTH0_DOMAIN: z.string().min(1),
  VITE_AUTH0_CLIENT_ID: z.string().min(1),
  VITE_AUTH0_AUDIENCE: z.string().min(1),
});

/** Dados de que o `Auth0Provider` precisa para iniciar o fluxo PKCE. */
export interface ConfiguracaoAuth0 {
  dominio: string;
  clientId: string;
  audience: string;
}

/** Configuração lida com sucesso, ou a lista de variáveis que faltaram. */
export type ResultadoConfiguracao =
  { ok: true; configuracao: ConfiguracaoAuth0 } | { ok: false; ausentes: readonly string[] };

/**
 * Lê e valida a configuração do Auth0 embutida no bundle.
 *
 * Variável ausente e variável vazia recebem o mesmo tratamento: um `.env` com
 * `VITE_AUTH0_CLIENT_ID=` é engano tão comum quanto esquecer a linha, e falhar
 * só num dos casos deixaria o outro passar até o login quebrar sem explicação.
 *
 * @returns Acerto com a configuração, ou os nomes das variáveis que faltam.
 */
export function lerConfiguracaoAuth0(): ResultadoConfiguracao {
  const resultado = configuracaoAuth0Schema.safeParse(import.meta.env);

  if (!resultado.success) {
    const ausentes = resultado.error.issues.map((problema) =>
      problema.path.map((parte) => String(parte)).join('.'),
    );
    return { ok: false, ausentes };
  }

  return {
    ok: true,
    configuracao: {
      dominio: resultado.data.VITE_AUTH0_DOMAIN,
      clientId: resultado.data.VITE_AUTH0_CLIENT_ID,
      audience: resultado.data.VITE_AUTH0_AUDIENCE,
    },
  };
}

/**
 * Monta o prefixo de toda rota da API.
 *
 * Quem chama é o `cliente.ts`, uma vez, ao carregar. Em produção a
 * `VITE_API_URL` traz a origem do Function App, e as chamadas saem para outro
 * domínio, liberadas pelo CORS de lá. Em desenvolvimento a variável não existe,
 * o prefixo fica `/api` relativo, e o proxy do `vite.config.ts` encaminha para o
 * `func start` na porta 7071.
 *
 * Num build de produção, a variável ausente não dá erro aqui: o prefixo cai em
 * `/api` do próprio SWA, que não tem API e responde `index.html` com status 200,
 * e toda chamada falha ao ler o corpo como JSON. Quem impede esse bundle de ser
 * publicado é a checagem da `VITE_API_URL` no passo "Construir o front" do
 * `deploy.yml`.
 *
 * A barra final é retirada porque o valor é colado à mão na variable do
 * repositório, e `https://origem/` resultaria em `https://origem//api`, rota que
 * o Function App não reconhece.
 *
 * @returns `https://origem-do-function-app/api`, ou `/api` quando a variável
 *   está ausente ou vazia.
 */
export function lerBaseDaApi(): string {
  const origem = (import.meta.env.VITE_API_URL ?? '').trim().replace(/\/+$/, '');
  return `${origem}/api`;
}
